/**
 * AlternativeStopsDiscovery - Shows alternative tour stops for studios >75mi away
 * Searches across ALL shows within 75 miles of the studio's location.
 * If alternatives found: shows up to 3 closest with show name, venue, dates, pricing.
 * If no alternatives: shows custom masterclass inquiry form.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { EventPricingResponse } from '../../api/events';
import { formatDateRange } from '../../api/events';
import { subscribeInterest } from '../../api/inquiries';
import { getFriendlyErrorMessage } from '../../api/client-error';

interface AlternativeStopsDiscoveryProps {
  pricing: EventPricingResponse;
  showId: string;
  locationQuery: string;
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function AlternativeStopsDiscovery({
  pricing,
  showId,
  locationQuery,
}: AlternativeStopsDiscoveryProps) {
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(() => {
    try {
      const tokensJson = localStorage.getItem('sda_auth_tokens');
      if (tokensJson) {
        const profileJson = localStorage.getItem('sda_user_profile');
        if (profileJson) {
          const profile = JSON.parse(profileJson) as { email?: string };
          return profile.email || '';
        }
      }
    } catch { /* ignore */ }
    return '';
  });
  const [notifyName, setNotifyName] = useState('');
  const [notifyStatus, setNotifyStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [notifyError, setNotifyError] = useState('');
  const alternatives = pricing.alternative_stops || [];
  const hasAlternatives = alternatives.length > 0;

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notifyEmail.trim() || !notifyName.trim()) return;
    setNotifyStatus('submitting');
    setNotifyError('');
    try {
      await subscribeInterest({ email: notifyEmail.trim(), name: notifyName.trim(), show_id: showId });
      setNotifyStatus('success');
    } catch (err) {
      setNotifyError(getFriendlyErrorMessage(err, 'Failed to subscribe'));
      setNotifyStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header — shown only when alternatives exist (announces them). When no
          alternatives, the merged "Host your class closer to the Theatre" block
          below carries the full message per SDA-157. */}
      {hasAlternatives && (
        <div className="bg-warm-50 rounded-xl p-6 border border-warm-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <svg
                className="h-6 w-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-warm-900 mb-1">
                Sorry, you are too far from this theater
              </h3>
              <p className="text-warm-600">
                You are {Math.round(pricing.distance_miles)} miles from this venue — too far
                for a cast member to visit your studio.
                {` However, we found ${alternatives.length === 1 ? 'a class' : `${alternatives.length} classes`} within 75 miles of you.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alternative Stops */}
      {hasAlternatives && !showInquiryForm && (
        <div className="space-y-4">
          <h4 className="font-semibold text-warm-900">
            Classes Within 75 Miles of Your Studio
          </h4>

          <div className="space-y-3">
            {alternatives.map((stop) => (
              <Link
                key={stop.tour_stop_id}
                to={`/events/${stop.tour_stop_id}?${locationQuery}`}
                className="block p-4 rounded-lg border border-warm-200 hover:border-primary-300 hover:bg-warm-50 transition-all group"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h5 className="font-semibold text-warm-900 group-hover:text-primary-600 transition-colors">
                      {stop.show_name}
                    </h5>
                    <p className="text-sm text-warm-600">
                      {stop.venue_name} — {stop.city}, {stop.state}
                    </p>
                    <p className="text-sm text-warm-400 mt-1">
                      {formatDateRange(stop.start_date, stop.end_date)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
                      {Math.round(stop.distance_miles)} mi
                    </span>
                    <p className="text-sm font-semibold text-warm-900 mt-1">
                      From {formatPrice(stop.starting_price_cents)}
                    </p>
                    {stop.travel_surcharge_cents > 0 && (
                      <p className="text-xs text-warm-400">
                        incl. {formatPrice(stop.travel_surcharge_cents)} travel
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Show Custom Inquiry Option */}
          <div className="border-t border-warm-200 pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowInquiryForm(true)}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-2"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              None of these work for me — I'd like a custom masterclass
            </button>
          </div>
        </div>
      )}

      {/* Custom Masterclass Inquiry */}
      {(!hasAlternatives || showInquiryForm) && (
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 border border-primary-200">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary-200 flex items-center justify-center flex-shrink-0">
              <svg className="h-5 w-5 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-warm-900 mb-1">
                Host your class closer to the Theatre
              </h4>
              <p className="text-warm-600 text-sm">
                Sorry, you are too far from this theatre. You are {Math.round(pricing.distance_miles)} miles from this venue - too far for a cast member to visit your studio. However, we do offer the option of hosting a class closer to the theatre for your studio if that was of interest!
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Link
              to={`/inquiry/${pricing.tour_stop_id}?${locationQuery}&type=hosted`}
              className="btn-primary w-full text-center block"
            >
              Request Custom Masterclass
            </Link>

            {showInquiryForm && hasAlternatives && (
              <button
                type="button"
                onClick={() => setShowInquiryForm(false)}
                className="btn w-full text-center border border-warm-300 text-warm-600 hover:bg-white"
              >
                Back to Alternatives
              </button>
            )}
          </div>

          {/* Notify Me Section */}
          <div className="border-t border-primary-200 pt-4 mt-4">
            <p className="text-sm text-warm-700 font-medium mb-3">
              Want to be notified when this show comes to your area?
            </p>
            {notifyStatus === 'success' ? (
              <p className="text-sm text-success-700 bg-success-50 rounded-lg px-4 py-3">
                You'll be notified when this show tours near you!
              </p>
            ) : (
              <form onSubmit={handleNotifySubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={notifyName}
                  onChange={(e) => setNotifyName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="flex-1 px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 px-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={notifyStatus === 'submitting'}
                  className="px-4 py-2 border-2 border-primary-500 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-50 transition-colors whitespace-nowrap"
                >
                  {notifyStatus === 'submitting' ? 'Subscribing...' : 'Notify Me'}
                </button>
              </form>
            )}
            {notifyStatus === 'error' && notifyError && (
              <p className="text-sm text-danger-600 mt-2">{notifyError}</p>
            )}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="flex items-start gap-3 text-sm text-warm-600 bg-warm-50 rounded-lg p-4">
        <svg
          className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p>
          <strong>Why the distance matters:</strong> For the best experience,
          we recommend booking classes within 75 miles of your studio. This ensures
          our cast members can provide their full energy and attention to your students.
        </p>
      </div>
    </div>
  );
}

export default AlternativeStopsDiscovery;
